import { useQueryClient } from "@tanstack/react-query";
import {
  deleteApprovalAlgorithm as apiDeleteApprovalAlgorithm,
  updateApprovalAlgorithm as apiUpdateApprovalAlgorithm,
  createApprovalAlgorithm,
  getApprovalAlgorithms,
} from "api/approvalAlgorithms";
import constate from "constate";
import { useFetchedState } from "hooks/useFetchedState";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { Map } from "immutable";
import { type ApprovalAlgorithmModel, type IApprovalAlgorithmSchema } from "models/ApprovalAlgorithmModel";
import { WORKFLOWS_FILTER_OPTIONS_QUERY_KEY, WORKFLOWS_QUERY_KEY } from "queries/approvalAlgorithmsQueries";
import { useCallback, useMemo } from "react";

const useApprovalAlgorithms = () => {
  const {
    data: approvalAlgorithms,
    forceLoadData: forceLoadApprovalAlgorithms,
    isLoading,
    loadData: loadApprovalAlgorithms,
    setData: setApprovalAlgorithms,
  } = useFetchedState(getApprovalAlgorithms, { ignoreLoadingState: true });
  const openGlobalErrorModal = useOpenGlobalErrorModal();
  const queryClient = useQueryClient();

  const invalidateWorkflowQueries = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [WORKFLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [WORKFLOWS_FILTER_OPTIONS_QUERY_KEY] }),
      ]),
    [queryClient],
  );

  const getMap = useCallback(async (): Promise<Map<string, ApprovalAlgorithmModel>> => {
    if (approvalAlgorithms) return approvalAlgorithms;
    try {
      return await getApprovalAlgorithms();
    } catch (error) {
      openGlobalErrorModal(error as Error);
      return Map();
    }
  }, [approvalAlgorithms, openGlobalErrorModal]);

  const saveApprovalAlgorithm = useCallback(
    async (approvalAlgorithm: ApprovalAlgorithmModel | IApprovalAlgorithmSchema) => {
      const savedAlgorithm = await createApprovalAlgorithm(approvalAlgorithm);
      const newApprovalAlgorithms = (await getMap()).set(savedAlgorithm.id, savedAlgorithm);
      setApprovalAlgorithms(newApprovalAlgorithms);
      void invalidateWorkflowQueries();
      return savedAlgorithm;
    },
    [getMap, invalidateWorkflowQueries, setApprovalAlgorithms],
  );

  const updateApprovalAlgorithm = useCallback(
    async (approvalAlgorithm: ApprovalAlgorithmModel | IApprovalAlgorithmSchema) => {
      try {
        const updatedAlgorithm = await apiUpdateApprovalAlgorithm(approvalAlgorithm);
        const newApprovalAlgorithms = (await getMap()).set(updatedAlgorithm.id, updatedAlgorithm);
        setApprovalAlgorithms(newApprovalAlgorithms);
        void invalidateWorkflowQueries();
        return updatedAlgorithm;
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }
    },
    [getMap, invalidateWorkflowQueries, openGlobalErrorModal, setApprovalAlgorithms],
  );

  const deleteApprovalAlgorithm = useCallback(
    async (approvalAlgorithmId: string) => {
      try {
        await apiDeleteApprovalAlgorithm(approvalAlgorithmId);
      } catch (error) {
        openGlobalErrorModal(error as Error);
        return;
      }

      setApprovalAlgorithms((current) =>
        current?.has(approvalAlgorithmId) ? current.setIn([approvalAlgorithmId, "isDeleted"], true) : current,
      );
      void invalidateWorkflowQueries();
    },
    [invalidateWorkflowQueries, openGlobalErrorModal, setApprovalAlgorithms],
  );

  const sortedApprovalAlgorithms = useMemo(
    () => approvalAlgorithms?.toList()?.sortBy((approvalAlgorithm) => approvalAlgorithm.name),
    [approvalAlgorithms],
  );

  return useMemo(
    () => ({
      actions: {
        deleteApprovalAlgorithm,
        forceLoadApprovalAlgorithms,
        loadApprovalAlgorithms,
        saveApprovalAlgorithm,
        updateApprovalAlgorithm,
      },
      state: { approvalAlgorithms, isLoading, sortedApprovalAlgorithms },
    }),
    [
      approvalAlgorithms,
      sortedApprovalAlgorithms,
      isLoading,
      loadApprovalAlgorithms,
      forceLoadApprovalAlgorithms,
      saveApprovalAlgorithm,
      updateApprovalAlgorithm,
      deleteApprovalAlgorithm,
    ],
  );
};

export const [ApprovalAlgorithmsProvider, useApprovalAlgorithmsContext] = constate(useApprovalAlgorithms);
